import React, { useEffect, useState } from 'react';
import { Flex, Text, VStack } from '@chakra-ui/react';
import {
  updatesGlobalDate,
  useAppDispatch,
  useAppSelector,
  useGetEntranceExitChartDataQuery,
  useLazyGetEntranceExitTableDataQuery,
} from '@spartalabs/pdc-core';
import { ChartCard } from 'components';
import TableCard from 'components/Cards/TableCard';
import LineChart from 'components/Charts/LineChart';
import format from 'date-fns/format';
import { useChart } from 'hooks/useChart';
import { DelayAnimation } from 'shared/DelayAnimation';
import { colors } from 'theme/components/colors';
import { emptyData } from 'utils/constants';
import { checkDateRange, downloadFile } from 'utils/functions';
import { tableLabels } from './const';

export const EntranceExit: React.FC = () => {
  const dispatch = useAppDispatch();
  const { globalDate, globalDateUpdate, globalPeriod } = useAppSelector(
    state => state.globalDataSlicer,
  );

  const [pickedPeriod, setPickedPeriod] = useState(globalPeriod);
  const [dateRange, setDateRange] = useState(checkDateRange(globalDate));
  const [chartLabels, setChartLabels] = useState(['']);
  const [chartData, setChartData] = useState<ChartData[] | null>(null);

  const {
    data: chartValues,
    isLoading: isLoadingChart,
    isFetching: isFetchingChart,
    isError: isErrorChart,
    refetch: refetchChart,
  } = useGetEntranceExitChartDataQuery({
    dimension: pickedPeriod,
    endDate: format(
      checkDateRange(dateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
    startDate: format(
      checkDateRange(dateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  const [
    fetchTable,
    {
      data: tableData,
      isLoading: isLoadingTable,
      isFetching: isFetchingTable,
      isError: isErrorTable,
    },
  ] = useLazyGetEntranceExitTableDataQuery();

  const { handleChartData, handleChartLabels } = useChart();

  useEffect(() => {
    fetchTable({ page: 1, quantity: 5, term: '' });
  }, []);

  useEffect(() => {
    setPickedPeriod(globalPeriod);
    if (globalDateUpdate) {
      setDateRange(globalDate);
      dispatch(updatesGlobalDate(false));
    }
  }, [globalDate, globalDateUpdate]);

  useEffect(() => {
    setPickedPeriod(globalPeriod);
    setDateRange(globalDate);
  }, [globalPeriod]);

  useEffect(() => {
    if (chartValues && chartValues?.length > 0) {
      const labels = handleChartLabels(pickedPeriod, chartValues);

      setChartLabels(labels);

      const chart = handleChartData(labels, [
        {
          label: 'Entrada',
          borderColor: colors.blue['100'],
          values: chartValues,
          accessor: 'entrance',
        },
        {
          label: 'Saída',
          borderColor: colors.blue['200'],
          values: chartValues,
          accessor: 'exit',
        },
      ]);

      setChartData(chart);
    }
  }, [chartValues]);

  return (
    <DelayAnimation>
      <VStack
        px={{ base: '16px', md: '32px' }}
        pb="20px"
        flex={1}
        minH="full"
        fontSize="xl"
      >
        <ChartCard
          cardName="entrace-exit"
          pickerType="date"
          title="Entrada e Saída de Manutenções"
          customTitle={
            <Text color="text.100">
              Gráfico de Entrada e Saída de <b>Manutenções</b>
            </Text>
          }
          subtitle="Comparativo sobre a relação entre entrada e saída de veículos em manutenções"
          hasFilter={false}
          picker={['Dia', 'Mês']}
          firstPicked={pickedPeriod}
          setPicked={setPickedPeriod}
          startDate={dateRange}
          setDateRange={setDateRange}
          w="100%"
          isError={isErrorChart}
          refetch={refetchChart}
        >
          <Flex w="100%" overflow="auto" justify="center">
            <LineChart
              loading={isLoadingChart || isFetchingChart}
              w="2000px"
              labels={chartLabels}
              data={chartData || emptyData}
            />
          </Flex>
        </ChartCard>
        <TableCard
          loading={isLoadingTable || isFetchingTable}
          fetchData={fetchTable}
          fetchDownload={async () =>
            await downloadFile('entranceExit', 'EntradaeSaida')
          }
          hasGlobalSearch
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Relatório de Veículos
            </Text>
          }
          subtitle="Listagem de veículos que passaram por manutenções"
          overflowX="auto"
          w="100%"
          labels={tableLabels}
          data={tableData}
          analyticsId="Entrada e Saída de Manutenções"
          isError={isErrorTable}
          refetch={fetchTable}
        />
      </VStack>
    </DelayAnimation>
  );
};
